import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import type {GuestCategory} from "crqlar-react-common/dist/models/profile/guestCategory.model";
import {GuestCategoryService} from "crqlar-react-common/dist/services/profile/guestCategory.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const compareGuestCategories = (first: GuestCategory, second: GuestCategory, field: string) => {
    if (field === 'updateDate') return (first.updateDate?.getTime() ?? 0) - (second.updateDate?.getTime() ?? 0)
    if (field === 'insertDate') return (first.insertDate?.getTime() ?? 0) - (second.insertDate?.getTime() ?? 0)
    if (field === 'id') return (first.id ?? '').localeCompare(second.id ?? '')
    return first.name.localeCompare(second.name)
}

const GuestCategoriesList = () => {
    useTrackScreenVisit('system guest categories');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<GuestCategory>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: guestCategory => guestCategory.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: guestCategory => guestCategory.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'insertDate',
            header: t('Created'),
            accessorFn: guestCategory => guestCategory.insertDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.insertDate)}</Box>,
            size: 200,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: guestCategory => guestCategory.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-guest-categories',
        columns,
        getRowId: guestCategory => guestCategory.id!,
        onRowClick: guestCategory => navigate(`/system/guestCategories/${guestCategory.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            // The catalog is one short list per organization, so search, sort and paging happen here.
            const query = searchQuery?.trim().toLocaleLowerCase()
            const guestCategories = (await GuestCategoryService.list())
                .filter(guestCategory => !query || guestCategory.name.toLocaleLowerCase().includes(query))

            if (sorting) {
                const direction = sorting.direction === 'DESC' ? -1 : 1
                guestCategories.sort((first, second) => direction * compareGuestCategories(first, second, sorting.field))
            }

            const start = pagination.pageIndex * pagination.pageSize
            return {
                content: guestCategories.slice(start, start + pagination.pageSize),
                totalElements: guestCategories.length
            };
        }
    });

    return (
        <SettingsListPage title={t('Guest categories')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create guest category')}
                            onClick={() => navigate('/system/guestCategories/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default GuestCategoriesList;
